import React, { forwardRef, useImperativeHandle, useEffect, useRef, useState } from "react";
import { Button, Form, FormField, FormInput, DateTimeInput } from "@/components/ui";
import { Card, CardContent } from "@/components/ui/card";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFieldArray, useForm } from "react-hook-form";
import { ActivityIndicator, View, StyleSheet, Text, ScrollView, TouchableOpacity, Modal } from "react-native";
import { z } from "zod";
import { securityFormSchema, mapCrewSecurityForToFrontend } from "./types";
import { useCrew } from "@/context/CrewProvider";
import { trpc } from "@/trpc/react";
import Toast from "react-native-toast-message";
import { addWeeks, format } from 'date-fns';
import { Ionicons } from '@expo/vector-icons';
import colors from '@/constants/Colors';

// Define the shape of a pending change
interface PendingChange {
  id: number;
  crew_id: number;
  key: string;
  value: string;
  status: number;
}

// Define the type for initialValues to fix TypeScript error
type FormValues = z.infer<typeof securityFormSchema>;

interface SecurityFormProps {
  onFormSuccess: () => void;
  onFormChange: (hasChanges: boolean) => void;
  pendingChanges: PendingChange[];
}

export type SecurityFormRef = {
  submit: () => void;
};

const SecurityForm = forwardRef<SecurityFormRef, SecurityFormProps>(
  ({ onFormSuccess, onFormChange, pendingChanges }, ref) => {
    const { currentCrewMember } = useCrew();

    const { data: securityDetails } = trpc.crew.getSecurityDetails.useQuery(
      currentCrewMember!.id,
      { enabled: !!currentCrewMember?.id }
    );

    const initialValues = currentCrewMember ? mapCrewSecurityForToFrontend(currentCrewMember, securityDetails) : {} as FormValues;
    const initialValuesRef = useRef<FormValues>(initialValues);

    // Modal state for showing pending change
    const [popoverVisible, setPopoverVisible] = useState(false);
    const [selectedChange, setSelectedChange] = useState<PendingChange | null>(null);

    const form = useForm<FormValues>({
      resolver: zodResolver(securityFormSchema),
      defaultValues: initialValues,
    });

    // Reset form with security details once they load
    useEffect(() => {
      if (currentCrewMember && securityDetails) {
        const updated = mapCrewSecurityForToFrontend(currentCrewMember, securityDetails);
        initialValuesRef.current = updated;
        form.reset(updated);
      }
    }, [securityDetails]);

    // Function to check if a field has pending changes
    const hasPendingChange = (fieldName: string): PendingChange | undefined => {
      // Handle complex field names for arrays like previous_addresses[0].address_line_1
      if (fieldName.includes('[') && fieldName.includes(']')) {
        // For array items, match the pattern in the key
        return pendingChanges.find(change => change.key.startsWith(fieldName));
      }
      return pendingChanges.find(change => change.key === fieldName);
    };

    // Function to display the pending value
    const displayPendingValue = (change: PendingChange) => {
      // Handle date fields differently
      if (change.key.includes('date') || change.key.includes('expiry') || change.key.includes('issue')) {
        try {
          const date = new Date(change.value);
          return format(date, 'dd/MM/yyyy');
        } catch (e) {
          return change.value;
        }
      }
      return change.value;
    };

    // Show popover with pending change details
    const showPendingChange = (change: PendingChange) => {
      setSelectedChange(change);
      setPopoverVisible(true);
    };

    // Track form changes and notify parent component
    useEffect(() => {
      const subscription = form.watch((formValues) => {
        // Compare current form values with initial values to detect changes
        let hasChanges = false;

        // Helper function to compare values, handling Date objects specially
        const isValueChanged = (current: any, initial: any) => {
          if (current instanceof Date && initial instanceof Date) {
            return current.getTime() !== initial.getTime();
          }
          return current !== initial && current !== undefined && current !== null && current !== '';
        };

        // Check regular fields
        for (const key in formValues) {
          if (key === 'previous_addresses' || key === 'previous_convictions') continue; // Skip arrays

          const fieldKey = key as keyof FormValues;
          const currentValue = formValues[fieldKey];
          const initialValue = initialValuesRef.current[fieldKey];

          if (isValueChanged(currentValue, initialValue)) {
            hasChanges = true;
            break;
          }
        }

        // Check array fields (previous_addresses and previous_convictions)
        const checkArrayChanges = (arrayName: 'previous_addresses' | 'previous_convictions') => {
          const currentArray = formValues[arrayName] as any[];
          const initialArray = initialValuesRef.current[arrayName] as any[] || [];

          // Different length means changes
          if (!currentArray || currentArray.length !== initialArray.length) {
            return true;
          }

          // Check each item in the array
          for (let i = 0; i < currentArray.length; i++) {
            const currentItem = currentArray[i];
            const initialItem = initialArray[i];

            for (const itemKey in currentItem) {
              if (isValueChanged(currentItem[itemKey], initialItem?.[itemKey])) {
                return true;
              }
            }
          }

          return false;
        };

        // Check array fields if regular fields haven't changed
        if (!hasChanges) {
          hasChanges = checkArrayChanges('previous_addresses') || checkArrayChanges('previous_convictions');
        }

        onFormChange(hasChanges);
      });

      return () => subscription.unsubscribe();
    }, [form, onFormChange]);

    const { mutate: updateCrewRequest } = trpc.crew.updateCrewRequest.useMutation({
      onSuccess(_response) {
        onFormSuccess();
        Toast.show({
          type: 'success',
          text1: 'Your profile request has been submitted!'
        });
      },
    });

    const { mutate: createTask } = trpc.task.createTask.useMutation({
      onSuccess(_response) { },
    });

    async function onSubmit(data: z.infer<typeof securityFormSchema>) {
      if (!currentCrewMember) return;

      try {
        const dirtyFields = form.formState.dirtyFields;

        const updatedCrew: { key: string; value: string }[] = Object.keys(dirtyFields)
          .map((fieldName) => {
            const value = data[fieldName as keyof typeof data];

            if (value === undefined || value === null || value === '') {
              return [];
            }

            if (Array.isArray(value)) {
              // Map array items to key-value format
              const arrayItems = value.flatMap((item, index) =>
                Object.entries(item)
                  .filter(([_, val]) => val !== undefined && val !== null && val !== '')
                  .map(([key, val]) => ({
                    key: `${fieldName}[${index}].${key}`,
                    value: val instanceof Date ? val.toString() : String(val),
                  }))
              );
              return arrayItems;
            } else {
              const stringValue = value instanceof Date ? value.toString() : String(value);
              return [{ key: fieldName, value: stringValue }];
            }
          })
          .flat()
          .filter(item => item.value !== '');

        updateCrewRequest({ id: currentCrewMember?.id, updateCrew: updatedCrew });

        createTask({
          title: `New Crew Profile Update for ${currentCrewMember?.name} (${updatedCrew.length} ${updatedCrew.length === 1 ? 'change' : 'changes'})`,
          description: `New Crew Profile Update for ${currentCrewMember?.name} (${updatedCrew.length} ${updatedCrew.length === 1 ? 'change' : 'changes'})`,
          priority: 1,
          tags: ["crew profile"],
          due_date: addWeeks(new Date(), 1),
          crew_id: currentCrewMember?.id,
          state_name: "Open",
          type: "crew.update",
          team_id: 2
        })
      } catch (error: Error | any) {
        Toast.show({
          type: 'error',
          text1: 'Failed to save settings, please try again.'
        });
      }
    }

    // Expose the submit method to the parent component
    useImperativeHandle(ref, () => ({
      submit: () => {
        form.handleSubmit(onSubmit)();
      }
    }));

    const { fields: addressFields, append: appendAddress, remove: removeAddress } = useFieldArray({
      control: form.control,
      name: "previous_addresses",
    });

    const { fields: convictionsFields, append: appendConvinction, remove: removeConviction } = useFieldArray({
      control: form.control,
      name: "previous_convictions",
    });

    // Custom input wrapper that includes a pending change indicator
    const FieldWithPendingIndicator = ({ name, children }: { name: string, children: React.ReactNode }) => {
      const pendingChange = hasPendingChange(name);

      return (
        <View style={styles.fieldWithIndicator}>
          <View style={styles.fieldContainer}>
            {children}
          </View>
          {pendingChange && (
            <TouchableOpacity
              style={styles.pendingIndicator}
              onPress={() => showPendingChange(pendingChange)}
            >
              <Ionicons name="alert-circle" size={20} color="#FF6B6B" />
            </TouchableOpacity>
          )}
        </View>
      );
    };

    return (
      <>
        <Card style={styles.card}>
          <View style={styles.headerContainer}>
            <View style={styles.iconWrapper}>
              <Ionicons name="shield-checkmark" size={18} color="white" />
            </View>
            <Text style={styles.headerTitle}>Security Details</Text>
          </View>
          <CardContent style={styles.cardContent}>
            <Form {...form}>
              <View style={styles.formContainer}>

                {/* Passport Section */}
                <View style={styles.sectionHeader}>
                  <Ionicons name="document" size={16} color="#222" />
                  <Text style={styles.sectionTitle}>Passport Details</Text>
                </View>

                <View style={styles.inputGroup}>
                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="card" size={18} color="#666" />
                      <Text style={styles.inputLabel}>Passport Number</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="passport_number"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="passport_number">
                          <FormInput
                            label=""
                            keyboardType="numeric"
                            style={styles.input}
                            {...field}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="calendar" size={18} color="#666" />
                      <Text style={styles.inputLabel}>Issue Date</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="passport_issue"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="passport_issue">
                          <DateTimeInput
                            {...field}
                            label=""
                            value={field.value || null}
                            style={styles.dateInput}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="calendar" size={18} color="#666" />
                      <Text style={styles.inputLabel}>Expiry Date</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="passport_expiry"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="passport_expiry">
                          <DateTimeInput
                            {...field}
                            label=""
                            value={field.value || null}
                            style={styles.dateInput}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="globe" size={18} color="#666" />
                      <Text style={styles.inputLabel}>Country of Issue</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="passport_country"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="passport_country">
                          <FormInput
                            label=""
                            style={styles.input}
                            {...field}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>

                  <View style={styles.formSection}>
                    <View style={styles.labelContainer}>
                      <Ionicons name="business" size={18} color="#666" />
                      <Text style={styles.inputLabel}>City of Issue</Text>
                    </View>
                    <FormField
                      control={form.control}
                      name="passport_city"
                      render={({ field }) => (
                        <FieldWithPendingIndicator name="passport_city">
                          <FormInput
                            label=""
                            style={styles.input}
                            {...field}
                          />
                        </FieldWithPendingIndicator>
                      )}
                    />
                  </View>
                </View>

                {/* Previous Addresses Section */}
                <View style={styles.sectionHeader}>
                  <Ionicons name="map" size={20} color="#222" />
                  <Text style={styles.sectionTitle}>Previous Addresses</Text>
                </View>

                {addressFields.map((address, index) => (
                  <View key={address.id} style={styles.cardItem}>
                    <View style={styles.cardItemHeader}>
                      <View style={styles.cardItemTitle}>
                        <Ionicons name="location" size={16} color="#666" />
                        <Text style={styles.cardItemTitleText}>Address {index + 1}</Text>
                      </View>
                      <Button
                        size="sm"
                        variant="destructive"
                        onPress={() => removeAddress(index)}
                        style={styles.deleteButton}
                      >
                        <Ionicons name="trash" size={16} color="white" style={{ marginRight: 4 }} />
                        <Text style={styles.deleteButtonText}>Remove</Text>
                      </Button>
                    </View>

                    <View style={styles.cardItemContent}>
                      <View style={styles.formSection}>
                        <View style={styles.labelContainer}>
                          <Ionicons name="calendar" size={16} color="#666" />
                          <Text style={styles.inputLabel}>Date Moved In</Text>
                        </View>
                        <FormField
                          control={form.control}
                          name={`previous_addresses.${index}.date_moved_in`}
                          render={({ field }) => (
                            <FieldWithPendingIndicator name={`previous_addresses[${index}].date_moved_in`}>
                              <DateTimeInput
                                {...field}
                                label=""
                                value={field.value || null}
                                style={styles.dateInput}
                              />
                            </FieldWithPendingIndicator>
                          )}
                        />
                      </View>

                      <View style={styles.formSection}>
                        <View style={styles.labelContainer}>
                          <Ionicons name="home" size={16} color="#666" />
                          <Text style={styles.inputLabel}>Address Line 1</Text>
                        </View>
                        <FormField
                          control={form.control}
                          name={`previous_addresses.${index}.address_line_1`}
                          render={({ field }) => (
                            <FieldWithPendingIndicator name={`previous_addresses[${index}].address_line_1`}>
                              <FormInput
                                label=""
                                style={styles.input}
                                {...field}
                              />
                            </FieldWithPendingIndicator>
                          )}
                        />
                      </View>

                      <View style={styles.formSection}>
                        <View style={styles.labelContainer}>
                          <Ionicons name="location" size={16} color="#666" />
                          <Text style={styles.inputLabel}>Address Line 2</Text>
                        </View>
                        <FormField
                          control={form.control}
                          name={`previous_addresses.${index}.address_line_2`}
                          render={({ field }) => (
                            <FieldWithPendingIndicator name={`previous_addresses[${index}].address_line_2`}>
                              <FormInput
                                label=""
                                style={styles.input}
                                {...field}
                              />
                            </FieldWithPendingIndicator>
                          )}
                        />
                      </View>

                      <View style={styles.formSection}>
                        <View style={styles.labelContainer}>
                          <Ionicons name="location" size={16} color="#666" />
                          <Text style={styles.inputLabel}>Address Line 3</Text>
                        </View>
                        <FormField
                          control={form.control}
                          name={`previous_addresses.${index}.address_line_3`}
                          render={({ field }) => (
                            <FieldWithPendingIndicator name={`previous_addresses[${index}].address_line_3`}>
                              <FormInput
                                label=""
                                style={styles.input}
                                {...field}
                              />
                            </FieldWithPendingIndicator>
                          )}
                        />
                      </View>

                      <View style={styles.formSection}>
                        <View style={styles.labelContainer}>
                          <Ionicons name="navigate" size={16} color="#666" />
                          <Text style={styles.inputLabel}>Postcode</Text>
                        </View>
                        <FormField
                          control={form.control}
                          name={`previous_addresses.${index}.address_postcode`}
                          render={({ field }) => (
                            <FieldWithPendingIndicator name={`previous_addresses[${index}].address_postcode`}>
                              <FormInput
                                label=""
                                style={styles.input}
                                {...field}
                              />
                            </FieldWithPendingIndicator>
                          )}
                        />
                      </View>
                    </View>
                  </View>
                ))}

                <Button
                  size="default"
                  variant="outline"
                  onPress={() => appendAddress({ address_line_1: '', address_line_2: '', address_line_3: '', address_postcode: '' })}
                  style={styles.addButton}
                >
                  <Ionicons name="add-circle" size={18} color="#666" style={{ marginRight: 6 }} />
                  <Text style={styles.addButtonText}>Add another address</Text>
                </Button>

                {/* Previous Convictions Section */}
                <View style={styles.sectionHeader}>
                  <Ionicons name="warning" size={20} color="#222" />
                  <Text style={styles.sectionTitle}>Previous Convictions</Text>
                </View>

                {convictionsFields.map((conviction, index) => (
                  <View key={conviction.id} style={styles.cardItem}>
                    <View style={styles.cardItemHeader}>
                      <View style={styles.cardItemTitle}>
                        <Ionicons name="alert-circle" size={16} color="#666" />
                        <Text style={styles.cardItemTitleText}>Conviction {index + 1}</Text>
                      </View>
                      <Button
                        size="sm"
                        variant="destructive"
                        onPress={() => removeConviction(index)}
                        style={styles.deleteButton}
                      >
                        <Ionicons name="trash" size={16} color="white" style={{ marginRight: 4 }} />
                        <Text style={styles.deleteButtonText}>Remove</Text>
                      </Button>
                    </View>

                    <View style={styles.cardItemContent}>
                      <View style={styles.formSection}>
                        <View style={styles.labelContainer}>
                          <Ionicons name="document-text" size={16} color="#666" />
                          <Text style={styles.inputLabel}>Conviction</Text>
                        </View>
                        <FormField
                          control={form.control}
                          name={`previous_convictions.${index}.conviction`}
                          render={({ field }) => (
                            <FieldWithPendingIndicator name={`previous_convictions[${index}].conviction`}>
                              <FormInput
                                label=""
                                style={styles.input}
                                {...field}
                              />
                            </FieldWithPendingIndicator>
                          )}
                        />
                      </View>

                      <View style={styles.formSection}>
                        <View style={styles.labelContainer}>
                          <Ionicons name="calendar" size={16} color="#666" />
                          <Text style={styles.inputLabel}>Date</Text>
                        </View>
                        <FormField
                          control={form.control}
                          name={`previous_convictions.${index}.date`}
                          render={({ field }) => (
                            <FieldWithPendingIndicator name={`previous_convictions[${index}].date`}>
                              <DateTimeInput
                                {...field}
                                label=""
                                value={field.value || null}
                                style={styles.dateInput}
                              />
                            </FieldWithPendingIndicator>
                          )}
                        />
                      </View>

                      <View style={styles.formSection}>
                        <View style={styles.labelContainer}>
                          <Ionicons name="list" size={16} color="#666" />
                          <Text style={styles.inputLabel}>Details</Text>
                        </View>
                        <FormField
                          control={form.control}
                          name={`previous_convictions.${index}.details`}
                          render={({ field }) => (
                            <FieldWithPendingIndicator name={`previous_convictions[${index}].details`}>
                              <FormInput
                                label=""
                                multiline
                                numberOfLines={4}
                                style={styles.textArea}
                                {...field}
                              />
                            </FieldWithPendingIndicator>
                          )}
                        />
                      </View>
                    </View>
                  </View>
                ))}

                <Button
                  size="default"
                  variant="outline"
                  onPress={() => appendConvinction({ conviction: '', date: null, details: '' })}
                  style={styles.addButton}
                >
                  <Ionicons name="add-circle" size={18} color="#666" style={{ marginRight: 6 }} />
                  <Text style={styles.addButtonText}>Add conviction</Text>
                </Button>
              </View>
            </Form>
          </CardContent>
        </Card>

        {/* Pending Change Popover */}
        <Modal
          animationType="fade"
          transparent={true}
          visible={popoverVisible}
          onRequestClose={() => setPopoverVisible(false)}
        >
          <TouchableOpacity
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={() => setPopoverVisible(false)}
          >
            <View style={styles.popoverContainer}>
              <View style={styles.popoverHeader}>
                <Ionicons name="alert-circle" size={20} color="#FF6B6B" />
                <Text style={styles.popoverTitle}>Pending Change</Text>
                <TouchableOpacity onPress={() => setPopoverVisible(false)}>
                  <Ionicons name="close" size={22} color="#666" />
                </TouchableOpacity>
              </View>
              {selectedChange && (
                <View style={styles.popoverContent}>
                  <Text style={styles.popoverLabel}>Field:</Text>
                  <Text style={styles.popoverValue}>
                    {selectedChange.key
                      .replace(/\[(\d+)\]/g, ' #$1 ') // Replace [0] with #0
                      .replace(/_/g, ' ')             // Replace underscores with spaces
                      .replace(/\./g, ' - ')          // Replace dots with hyphens
                      .replace(/\b\w/g, l => l.toUpperCase())} {/* Capitalize first letter */}
                  </Text>

                  <Text style={styles.popoverLabel}>New Value:</Text>
                  <Text style={styles.popoverValue}>{displayPendingValue(selectedChange)}</Text>

                  <Text style={styles.popoverNote}>This change is pending approval.</Text>
                </View>
              )}
            </View>
          </TouchableOpacity>
        </Modal>
      </>
    );
  }
);

const styles = StyleSheet.create({
  card: {
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 2,
    backgroundColor: '#fff',
    overflow: 'hidden',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#eee',
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000',
    marginLeft: 8,
  },
  iconWrapper: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#222',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardContent: {
    padding: 0,
  },
  formContainer: {
    padding: 0,
  },
  inputGroup: {
    backgroundColor: '#fff',
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 4,
    marginBottom: 16,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    paddingHorizontal: 16,
    marginTop: 5,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#000',
    marginLeft: 6,
  },
  formSection: {
    marginBottom: 12,
  },
  labelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  inputLabel: {
    marginLeft: 6,
    fontSize: 13,
    fontWeight: '500',
    color: '#444',
  },
  input: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    height: 44,
  },
  textArea: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    minHeight: 100,
    textAlignVertical: 'top',
  },
  buttonContainer: {
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  submitButton: {
    borderRadius: 8,
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFF000',
    textTransform: 'uppercase',
  },
  dateInput: {
    backgroundColor: '#fff',
    height: 44,
    marginVertical: 0,
    paddingVertical: 0,
  },
  cardItem: {
    backgroundColor: '#fff',
    borderRadius: 8,
    marginBottom: 16,
    marginHorizontal: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#eee',
  },
  cardItemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#f9f9f9',
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  cardItemTitle: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  cardItemTitleText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 6,
  },
  cardItemContent: {
    padding: 12,
  },
  deleteButton: {
    backgroundColor: '#ff5252',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  deleteButtonText: {
    color: 'white',
    fontSize: 11,
    fontWeight: '500',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    marginHorizontal: 16,
    marginBottom: 16,
    paddingVertical: 10,
  },
  addButtonText: {
    color: '#666',
    fontWeight: '500',
    fontSize: 14,
  },
  fieldWithIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  fieldContainer: {
    flex: 1,
  },
  pendingIndicator: {
    marginLeft: 8,
    padding: 4,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  popoverContainer: {
    width: '85%',
    backgroundColor: 'white',
    borderRadius: 12,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  popoverHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f9f9f9',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  popoverTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    flex: 1,
    marginLeft: 8,
  },
  popoverContent: {
    padding: 16,
  },
  popoverLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#666',
    marginBottom: 4,
  },
  popoverValue: {
    fontSize: 16,
    color: '#333',
    marginBottom: 16,
  },
  popoverNote: {
    fontSize: 14,
    color: '#FF6B6B',
    fontStyle: 'italic',
    marginTop: 8,
  },
});

export default SecurityForm;